import { validatedDisplayView, extendDisplayView } from "../../i18n/display-view.js";
import { portalText } from "../../i18n/portal.js";
/**
 * Area `account-security` of the console gateway — over `apps/api/src/routes/account-security/` (design §27.9.2).
 * Owned by plan-014 unit **C2**.
 *
 *   readAccountSecurity            `getAccountSecurity`                    the policy, domains, ranges, preflights
 *   previewAccountSignInPolicy     `previewAccountSignInPolicy`            who a choice would affect
 *   saveAccountSignInPolicy        `updateAccountSignInPolicy`             step-up, If-Match (enforced or a draft)
 *   addAccountVerifiedDomain       `createAccountVerifiedDomain`           step-up
 *   verifyAccountVerifiedDomain    `verifyAccountVerifiedDomain`           step-up; 503 when DNS could not be checked
 *   setAccountVerifiedDomainAutoJoin `updateAccountVerifiedDomainAutoJoin` step-up
 *   removeAccountVerifiedDomain    `deleteAccountVerifiedDomain`           step-up
 *   addAccountAllowedNetwork       `createAccountAllowedNetwork`           step-up
 *   removeAccountAllowedNetwork    `deleteAccountAllowedNetwork`           step-up
 *   confirmIdentity                `stepUpAuthSession`                     password, authenticator code or recovery code
 *
 * ── THE GENERATED CLIENT ─────────────────────────────────────────────────────────────────────────────────────
 * The operations are in C2's fragments; the merged spec and `@wetalk/api-client`'s generated client are regenerated by
 * the api-client lock holder (plan-014 §2.3), never by a unit. So this slice reads them through {@link operationsOf},
 * a structural view of the client with exactly these methods (C1's and B2's precedent): it compiles before and after
 * the regeneration, and a client that still lacks one raises naming the regeneration — never an empty answer.
 *
 * `confirmIdentity` is the step-up every change on the Security page (and the ownership transfer) asks for: the
 * password, the authenticator's six digits, or one recovery code — so a person who signs in only with Google or
 * Microsoft can confirm with their authenticator (plan-014 C2 hand-off).
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

// ── views ─────────────────────────────────────────────────────────────────────────────────────────────────

export type SignInPolicyKindView = "any_method" | "authenticator_required" | "single_sign_on_only";

export interface SignInPolicyView {
  readonly accountSignInPolicyId: string;
  readonly kind: SignInPolicyKindView;
  readonly state: "draft" | "enforced" | "superseded";
  readonly identityProviderId: string | null;
  readonly policyRevision: number;
  readonly enforcedAt: string | null;
  readonly savedAt: string;
}

export type AffectedReasonView = "add_authenticator" | "no_recovery_code" | "loses_access";

export interface SignInPolicyPreflightView {
  readonly kind: SignInPolicyKindView;
  readonly memberCount: number;
  readonly affectedPerson: readonly {
    readonly personId: string;
    readonly displayName: string | null;
    readonly email: string;
    readonly accountRole: string | null;
    readonly reason: AffectedReasonView;
  }[];
  readonly ownerCount: number;
  readonly ownerRecoveryCount: number;
  readonly ownerRecoveryReady: boolean;
  readonly administratorCount: number;
  readonly administratorWithAuthenticatorCount: number;
  readonly ownerRecoveryCodeLeast: number | null;
}

export type VerifiedDomainStateView = "pending" | "verified" | "lapsed";
export type DomainCheckOutcomeView = "found" | "not_found_yet" | "could_not_check";
export type AutoJoinRoleView = "viewer" | "member" | "developer" | "campaign_manager" | "billing";

export interface VerifiedDomainView {
  readonly ssoDomainId: string;
  readonly domain: string;
  readonly state: VerifiedDomainStateView;
  readonly txtName: string;
  readonly txtValue: string;
  readonly identityProviderId: string | null;
  readonly verifiedAt: string | null;
  readonly lapsedAt: string | null;
  readonly lastCheckedAt: string | null;
  readonly lastCheckOutcome: DomainCheckOutcomeView | null;
  readonly autoJoin: { readonly memberRole: AutoJoinRoleView; readonly workspace: readonly { readonly workspaceId: string; readonly name: string }[] } | null;
}

export interface AllowedNetworkView {
  readonly accountAllowedNetworkId: string;
  readonly network: string;
  readonly label: string;
  readonly addedByPersonId: string;
  readonly addedAt: string;
}

export interface AccountSecurityView {
  readonly policy: SignInPolicyView | null;
  readonly draft: SignInPolicyView | null;
  readonly policyRevision: number;
  readonly verifiedDomain: readonly VerifiedDomainView[];
  readonly allowedNetwork: readonly AllowedNetworkView[];
  readonly identityProvider: readonly { readonly identityProviderId: string; readonly kind: "google" | "microsoft_entra" }[];
  readonly clientAddress: { readonly address: string | null; readonly insideAllowedNetwork: boolean | null };
  readonly preflight: Readonly<Record<SignInPolicyKindView, SignInPolicyPreflightView>>;
}

export interface SavedSignInPolicyView {
  readonly state: "enforced" | "draft";
  readonly policyRevision: number;
  readonly draftReason: "sign_in_policy_domain_unverified" | null;
  readonly unchanged: boolean;
  readonly preflight: SignInPolicyPreflightView;
}

export type IdentityConfirmationFactor = "password" | "totp" | "recovery_code";

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** The organization's sign-in policy, verified domains, allowed ranges and the preflight of each policy. */
    readAccountSecurity(accountId: string): Promise<AccountSecurityView>;
    /** Who a policy would affect, saving nothing. */
    previewAccountSignInPolicy(accountId: string, kind: SignInPolicyKindView): Promise<SignInPolicyPreflightView>;
    /** Sets how people sign in; `revision` is the enforced one read (0 for none). Answers `step_up_required` until confirmed. */
    saveAccountSignInPolicy(accountId: string, kind: SignInPolicyKindView, revision: number): Promise<SavedSignInPolicyView>;
    addAccountVerifiedDomain(accountId: string, domain: string): Promise<VerifiedDomainView>;
    /** One DNS check; answers `domain_check_unavailable` (503) when DNS could not be checked — never "not found". */
    verifyAccountVerifiedDomain(accountId: string, ssoDomainId: string): Promise<VerifiedDomainView & { readonly outcome: DomainCheckOutcomeView }>;
    setAccountVerifiedDomainAutoJoin(
      accountId: string,
      ssoDomainId: string,
      memberRole: AutoJoinRoleView | null,
      workspaceId: readonly string[],
    ): Promise<VerifiedDomainView>;
    removeAccountVerifiedDomain(accountId: string, ssoDomainId: string): Promise<void>;
    addAccountAllowedNetwork(accountId: string, network: string, label: string): Promise<AllowedNetworkView>;
    removeAccountAllowedNetwork(accountId: string, accountAllowedNetworkId: string): Promise<void>;
    /** The step-up: the password, the authenticator's code, or one recovery code (`POST /v1/session/step-up`). */
    confirmIdentity(factor: IdentityConfirmationFactor, secret: string): Promise<void>;
  }
}

export type AccountSecurityGatewaySlice = Pick<
  ConsoleGateway,
  | "readAccountSecurity"
  | "previewAccountSignInPolicy"
  | "saveAccountSignInPolicy"
  | "addAccountVerifiedDomain"
  | "verifyAccountVerifiedDomain"
  | "setAccountVerifiedDomainAutoJoin"
  | "removeAccountVerifiedDomain"
  | "addAccountAllowedNetwork"
  | "removeAccountAllowedNetwork"
  | "confirmIdentity"
>;

/** This area's rows of `GATEWAY_GAP`: none — every operation is in C2's fragments. */
export const accountSecurityGap: readonly GatewayGap[] = [];

// ── the client, structurally ──────────────────────────────────────────────────────────────────────────────

type Header = Readonly<Record<string, string>>;
type AccountPath = { readonly account_id: string };

interface AccountSecurityOperations {
  getAccountSecurity(request: { path: AccountPath }): Promise<ApiResult>;
  previewAccountSignInPolicy(request: { path: AccountPath; body?: unknown }): Promise<ApiResult>;
  updateAccountSignInPolicy(request: { path: AccountPath; body?: unknown; header?: Header }): Promise<ApiResult>;
  createAccountVerifiedDomain(request: { path: AccountPath; body?: unknown }): Promise<ApiResult>;
  verifyAccountVerifiedDomain(request: { path: AccountPath & { readonly sso_domain_id: string } }): Promise<ApiResult>;
  updateAccountVerifiedDomainAutoJoin(request: { path: AccountPath & { readonly sso_domain_id: string }; body?: unknown }): Promise<ApiResult>;
  deleteAccountVerifiedDomain(request: { path: AccountPath & { readonly sso_domain_id: string } }): Promise<ApiResult>;
  createAccountAllowedNetwork(request: { path: AccountPath; body?: unknown }): Promise<ApiResult>;
  deleteAccountAllowedNetwork(request: { path: AccountPath & { readonly account_allowed_network_id: string } }): Promise<ApiResult>;
  stepUpAuthSession(request: { body?: unknown }): Promise<ApiResult>;
}

/** The client's methods for this area, by name. Raises for one the generated client does not carry yet. */
function operationsOf(client: WeTalkApiClient): AccountSecurityOperations {
  const held = client as unknown as Partial<Record<keyof AccountSecurityOperations, unknown>>;
  return new Proxy({} as AccountSecurityOperations, {
    get(_target, name: string) {
      const found = held[name as keyof AccountSecurityOperations];
      if (typeof found !== "function") {
        throw new Error(
          portalText("shell", "the_generated_api_client_has_no_value_regenerate_it_pnpm_a_58552af3", { v0: String(name) }),
        );
      }
      return (found as (...argument: unknown[]) => unknown).bind(client);
    },
  });
}

// ── decoding ──────────────────────────────────────────────────────────────────────────────────────────────

const KIND: readonly SignInPolicyKindView[] = ["any_method", "authenticator_required", "single_sign_on_only"];
const DOMAIN_STATE: readonly VerifiedDomainStateView[] = ["pending", "verified", "lapsed"];
const OUTCOME: readonly DomainCheckOutcomeView[] = ["found", "not_found_yet", "could_not_check"];
const AUTO_JOIN_ROLE: readonly AutoJoinRoleView[] = ["viewer", "member", "developer", "campaign_manager", "billing"];
const REASON: readonly AffectedReasonView[] = ["add_authenticator", "no_recovery_code", "loses_access"];

function dataOf(result: ApiResult, operationId: string): Readonly<Record<string, unknown>> {
  return readRecord(readRecord(assertOk(result), operationId)["data"], `${operationId}.data`);
}

function textOrNull(value: unknown, path: string): string | null {
  return value === null || value === undefined ? null : readText(value, path);
}

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const text = readText(value, path);
  const found = allowed.find((one) => one === text);
  if (found === undefined) throw new Error(`${path}: "${text}" is not one of ${allowed.join(", ")}`);
  return found;
}

function policyOf(value: unknown, path: string): SignInPolicyView | null {
  if (value === null || value === undefined) return null;
  const row = readRecord(value, path);
  return {
    accountSignInPolicyId: readText(row["account_sign_in_policy_id"], `${path}.account_sign_in_policy_id`),
    kind: oneOf(KIND, row["kind"], `${path}.kind`),
    state: oneOf(["draft", "enforced", "superseded"] as const, row["state"], `${path}.state`),
    identityProviderId: textOrNull(row["identity_provider_id"], `${path}.identity_provider_id`),
    policyRevision: readWholeNumber(row["policy_revision"], `${path}.policy_revision`),
    enforcedAt: textOrNull(row["enforced_at"], `${path}.enforced_at`),
    savedAt: readText(row["saved_at"], `${path}.saved_at`),
  };
}

export function preflightOf(value: unknown, path: string): SignInPolicyPreflightView {
  const row = readRecord(value, path);
  const least = row["owner_recovery_code_least"];
  return {
    kind: oneOf(KIND, row["kind"], `${path}.kind`),
    memberCount: readWholeNumber(row["member_count"], `${path}.member_count`),
    affectedPerson: readArray(row["affected_person"], `${path}.affected_person`).map((one, index) => {
      const person = readRecord(one, `${path}.affected_person[${index}]`);
      return {
        personId: readText(person["person_id"], "person_id"),
        displayName: textOrNull(person["display_name"], "display_name"),
        email: readText(person["email"], "email"),
        accountRole: textOrNull(person["account_role"], "account_role"),
        reason: oneOf(REASON, person["reason"], "reason"),
      };
    }),
    ownerCount: readWholeNumber(row["owner_count"], `${path}.owner_count`),
    ownerRecoveryCount: readWholeNumber(row["owner_recovery_count"], `${path}.owner_recovery_count`),
    ownerRecoveryReady: readBoolean(row["owner_recovery_ready"], `${path}.owner_recovery_ready`),
    administratorCount: readWholeNumber(row["administrator_count"], `${path}.administrator_count`),
    administratorWithAuthenticatorCount: readWholeNumber(row["administrator_with_authenticator_count"], `${path}.administrator_with_authenticator_count`),
    ownerRecoveryCodeLeast: least === null || least === undefined ? null : readWholeNumber(least, `${path}.owner_recovery_code_least`),
  };
}

export function domainOf(value: unknown, path: string): VerifiedDomainView {
  const row = readRecord(value, path);
  const autoJoin = row["auto_join"];
  const outcome = row["last_check_outcome"];
  return {
    ssoDomainId: readText(row["sso_domain_id"], `${path}.sso_domain_id`),
    domain: readText(row["domain"], `${path}.domain`),
    state: oneOf(DOMAIN_STATE, row["state"], `${path}.state`),
    txtName: readText(row["txt_name"], `${path}.txt_name`),
    txtValue: readText(row["txt_value"], `${path}.txt_value`),
    identityProviderId: textOrNull(row["identity_provider_id"], `${path}.identity_provider_id`),
    verifiedAt: textOrNull(row["verified_at"], `${path}.verified_at`),
    lapsedAt: textOrNull(row["lapsed_at"], `${path}.lapsed_at`),
    lastCheckedAt: textOrNull(row["last_checked_at"], `${path}.last_checked_at`),
    lastCheckOutcome: outcome === null || outcome === undefined ? null : oneOf(OUTCOME, outcome, `${path}.last_check_outcome`),
    autoJoin:
      autoJoin === null || autoJoin === undefined
        ? null
        : (() => {
            const join = readRecord(autoJoin, `${path}.auto_join`);
            return {
              memberRole: oneOf(AUTO_JOIN_ROLE, join["member_role"], `${path}.auto_join.member_role`),
              workspace: readArray(join["workspace"], `${path}.auto_join.workspace`).map((one) => {
                const workspace = readRecord(one, `${path}.auto_join.workspace`);
                return { workspaceId: readText(workspace["workspace_id"], "workspace_id"), name: readText(workspace["name"], "name") };
              }),
            };
          })(),
  };
}

function networkOf(value: unknown, path: string): AllowedNetworkView {
  const row = readRecord(value, path);
  return validatedDisplayView({
    accountAllowedNetworkId: readText(row["account_allowed_network_id"], `${path}.account_allowed_network_id`),
    network: readText(row["network"], `${path}.network`),
    get label() { return readText(row["label"], `${path}.label`); },
    addedByPersonId: readText(row["added_by_person_id"], `${path}.added_by_person_id`),
    addedAt: readText(row["added_at"], `${path}.added_at`),
  });
}

export function accountSecurityOf(data: Readonly<Record<string, unknown>>): AccountSecurityView {
  const address = readRecord(data["client_address"], "client_address");
  const inside = address["inside_allowed_network"];
  const preflight = readRecord(data["preflight"], "preflight");
  return {
    policy: policyOf(data["policy"], "policy"),
    draft: policyOf(data["draft"], "draft"),
    policyRevision: readWholeNumber(data["policy_revision"], "policy_revision"),
    verifiedDomain: readArray(data["verified_domain"], "verified_domain").map((one, index) => domainOf(one, `verified_domain[${index}]`)),
    allowedNetwork: readArray(data["allowed_network"], "allowed_network").map((one, index) => networkOf(one, `allowed_network[${index}]`)),
    identityProvider: readArray(data["identity_provider"], "identity_provider").map((one) => {
      const row = readRecord(one, "identity_provider");
      return {
        identityProviderId: readText(row["identity_provider_id"], "identity_provider_id"),
        kind: oneOf(["google", "microsoft_entra"] as const, row["kind"], "identity_provider.kind"),
      };
    }),
    clientAddress: { address: textOrNull(address["address"], "client_address.address"), insideAllowedNetwork: inside === null || inside === undefined ? null : readBoolean(inside, "inside_allowed_network") },
    preflight: {
      any_method: preflightOf(preflight["any_method"], "preflight.any_method"),
      authenticator_required: preflightOf(preflight["authenticator_required"], "preflight.authenticator_required"),
      single_sign_on_only: preflightOf(preflight["single_sign_on_only"], "preflight.single_sign_on_only"),
    },
  };
}

// ── the slice ─────────────────────────────────────────────────────────────────────────────────────────────

export function accountSecurityApiSlice(client: WeTalkApiClient, _context: SliceContext): AccountSecurityGatewaySlice {
  const operation = operationsOf(client);
  return {
    async readAccountSecurity(accountId) {
      return accountSecurityOf(dataOf(await operation.getAccountSecurity({ path: { account_id: accountId } }), "getAccountSecurity"));
    },
    async previewAccountSignInPolicy(accountId, kind) {
      return preflightOf(
        dataOf(await operation.previewAccountSignInPolicy({ path: { account_id: accountId }, body: { kind } }), "previewAccountSignInPolicy"),
        "previewAccountSignInPolicy",
      );
    },
    async saveAccountSignInPolicy(accountId, kind, revision) {
      const data = dataOf(
        await operation.updateAccountSignInPolicy({ path: { account_id: accountId }, body: { kind }, header: { "if-match": `"${String(revision)}"` } }),
        "updateAccountSignInPolicy",
      );
      const draftReason = data["draft_reason"];
      return {
        state: oneOf(["enforced", "draft"] as const, data["state"], "state"),
        policyRevision: readWholeNumber(data["policy_revision"], "policy_revision"),
        draftReason: draftReason === "sign_in_policy_domain_unverified" ? "sign_in_policy_domain_unverified" : null,
        unchanged: readBoolean(data["unchanged"], "unchanged"),
        preflight: preflightOf(data["preflight"], "preflight"),
      };
    },
    async addAccountVerifiedDomain(accountId, domain) {
      return domainOf(dataOf(await operation.createAccountVerifiedDomain({ path: { account_id: accountId }, body: { domain } }), "createAccountVerifiedDomain"), "domain");
    },
    async verifyAccountVerifiedDomain(accountId, ssoDomainId) {
      const data = dataOf(
        await operation.verifyAccountVerifiedDomain({ path: { account_id: accountId, sso_domain_id: ssoDomainId } }),
        "verifyAccountVerifiedDomain",
      );
      return extendDisplayView(domainOf(data, "domain"), { outcome: oneOf(OUTCOME, data["outcome"], "outcome") });
    },
    async setAccountVerifiedDomainAutoJoin(accountId, ssoDomainId, memberRole, workspaceId) {
      return domainOf(
        dataOf(
          await operation.updateAccountVerifiedDomainAutoJoin({
            path: { account_id: accountId, sso_domain_id: ssoDomainId },
            body: { member_role: memberRole, workspace_id: [...workspaceId] },
          }),
          "updateAccountVerifiedDomainAutoJoin",
        ),
        "domain",
      );
    },
    async removeAccountVerifiedDomain(accountId, ssoDomainId) {
      assertOk(await operation.deleteAccountVerifiedDomain({ path: { account_id: accountId, sso_domain_id: ssoDomainId } }));
    },
    async addAccountAllowedNetwork(accountId, network, label) {
      return networkOf(dataOf(await operation.createAccountAllowedNetwork({ path: { account_id: accountId }, body: { network, label } }), "createAccountAllowedNetwork"), "network");
    },
    async removeAccountAllowedNetwork(accountId, accountAllowedNetworkId) {
      assertOk(await operation.deleteAccountAllowedNetwork({ path: { account_id: accountId, account_allowed_network_id: accountAllowedNetworkId } }));
    },
    async confirmIdentity(factor, secret) {
      assertOk(await operation.stepUpAuthSession({ body: factor === "password" ? { factor, password: secret } : { factor, code: secret } }));
    },
  };
}
